import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { timeAgo } from '@/utils/datetime';
import {
  List, CaretLeft, UserCircle, Users, CreditCard, Pulse, Plus, PencilSimple,
  Vault,
  Trash, ArrowsLeftRight, Lock, LockOpen, UserPlus, UserMinus, UserCheck,
  PaperPlaneTilt, CheckCircle, XCircle, Buildings, ChartLineUp, FileArrowUp,
  Wallet, Note, Compass,
} from '@phosphor-icons/react';
import { startProductTour, ACCOUNT_MENU_EVENT } from './OnboardingTour';

const ICONS = {
  Plus, PencilSimple, Trash, ArrowsLeftRight, Lock, LockOpen, UserPlus,
  UserMinus, UserCheck, PaperPlaneTilt, CheckCircle, XCircle, UserCircle,
  Buildings, ChartLineUp, FileArrowUp, CreditCard, Wallet, Note,
  Activity: Pulse,
};

/**
 * AccountMenu — single button that opens a dropdown with Profile / Team / Billing /
 * Activity Logs.  The activity-logs entry expands inline (swaps the menu body
 * to a scrollable list with a back arrow) so everything lives in one popover.
 *
 * Mounted next to the LiveTicker filter button for VCs/Incubators, and stand-alone
 * in the Startup header.  `role` controls the navigate-base path (`/vc` vs `/startup`).
 * `size` adjusts the button to match its neighbour (ticker-gear button uses 36x36).
 */
const AccountMenu = ({ role = 'vc', accentColor = '#6366f1', size = 36 }) => {
  const navigate = useNavigate();
  const wrapperRef = useRef(null);
  const lastSeenRef = useRef(localStorage.getItem('audit_last_seen') || null);

  const [open, setOpen] = useState(false);
  const [tourPinned, setTourPinned] = useState(false); // held open by the product tour
  const [view, setView] = useState('main'); // 'main' | 'logs'
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  const base = role === 'vc' ? '/vc' : '/startup';
  const menuItems = [
    { key: 'profile', label: 'Profile', icon: UserCircle, path: `${base}/profile` },
    { key: 'team', label: 'Team', icon: Users, path: `${base}/team` },
    { key: 'billing', label: 'Billing', icon: CreditCard, path: `${base}/billing` },
  ];
  if (role === 'startup') {
    // (Iter 46) Vault moved from the left nav to the right-side account menu.
    menuItems.push({ key: 'vault', label: 'Vault', icon: Vault, path: `${base}/vault` });
  }
  const fetchLogs = async (silent = false) => {
    if (!silent) setLogsLoading(true);
    try {
      const res = await api.get('/audit-logs?limit=50');
      const next = res.data?.logs || [];
      setLogs(next);
      if (next.length && lastSeenRef.current) {
        setHasNew(new Date(next[0].created_at) > new Date(lastSeenRef.current));
      } else if (next.length) {
        setHasNew(true);
      } else {
        setHasNew(false);
      }
    } catch {
      /* swallow — interceptor handles 5xx */
    } finally {
      if (!silent) setLogsLoading(false);
    }
  };

  // Initial fetch + 60s polling so the new-activity dot updates without manual opens.
  useEffect(() => {
    fetchLogs(true);
    const t = setInterval(() => fetchLogs(true), 60000);
    return () => clearInterval(t);
  }, []);

  // Mark all-seen the moment the user opens the Activity-Logs subview.
  useEffect(() => {
    if (open && view === 'logs' && logs.length) {
      const newest = logs[0].created_at;
      lastSeenRef.current = newest;
      localStorage.setItem('audit_last_seen', newest);
      setHasNew(false);
      fetchLogs(false);
    }
  }, [open, view]);

  // Reset to main view when the popover closes.
  useEffect(() => {
    if (!open) setView('main');
  }, [open]);

  // The product tour drives the dropdown open so it can spotlight the "Product
  // Tour" row itself. While pinned we ignore click-outside — otherwise clicking
  // the tour's own Next button would unmount the element being highlighted.
  useEffect(() => {
    const onTourToggle = (e) => {
      const wantOpen = !!e.detail?.open;
      setTourPinned(wantOpen);
      setOpen(wantOpen);
      if (wantOpen) setView('main');
    };
    window.addEventListener(ACCOUNT_MENU_EVENT, onTourToggle);
    return () => window.removeEventListener(ACCOUNT_MENU_EVENT, onTourToggle);
  }, []);

  // Click-outside closes.
  useEffect(() => {
    if (!open || tourPinned) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, tourPinned]);

  const handleNav = (path) => {
    setOpen(false);
    navigate(path);
  };

  const buttonStyle = { width: size, height: size };

  return (
    <div className="relative" ref={wrapperRef} data-testid="account-menu-root">
      <button
        onClick={() => setOpen(!open)}
        data-testid="account-menu-toggle"
        title="Account menu"
        aria-label="Account menu"
        style={buttonStyle}
        className="relative flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
      >
        <List size={16} weight="bold" />
        {hasNew && (
          <span
            data-testid="account-menu-new-dot"
            className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full ring-2 ring-surface"
            style={{ backgroundColor: accentColor }}
          />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-[300px] max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-lg shadow-2xl z-[60] overflow-hidden"
          data-testid="account-menu-dropdown"
        >
          {view === 'main' ? (
            <div className="py-1">
              {menuItems.map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.key}
                    onClick={() => handleNav(m.path)}
                    data-testid={`account-menu-${m.key}`}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-text-primary hover:bg-background transition-colors"
                  >
                    <Icon size={15} weight="bold" className="text-text-secondary flex-shrink-0" />
                    <span className="text-sm font-medium flex-1">{m.label}</span>
                  </button>
                );
              })}
              <div className="my-1 border-t border-border" />
              <button
                onClick={() => { setOpen(false); startProductTour(); }}
                data-testid="account-menu-product-tour"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-text-primary hover:bg-background transition-colors"
              >
                <Compass size={15} weight="bold" className="text-text-secondary flex-shrink-0" />
                <span className="text-sm font-medium flex-1">Product Tour</span>
              </button>
              <button
                onClick={() => setView('logs')}
                data-testid="account-menu-activity-logs"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-text-primary hover:bg-background transition-colors"
              >
                <Pulse size={15} weight="bold" className="text-text-secondary flex-shrink-0" />
                <span className="text-sm font-medium flex-1">Activity Logs</span>
                {hasNew && (
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: accentColor }}
                  />
                )}
              </button>
            </div>
          ) : (
            // ============ ACTIVITY LOGS SUBVIEW ============
            <div data-testid="account-menu-logs-panel">
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
                <button
                  onClick={() => setView('main')}
                  data-testid="account-menu-back-btn"
                  className="flex items-center gap-1.5 text-xs text-text-secondary hover:text-text-primary"
                >
                  <CaretLeft size={12} weight="bold" /> Back
                </button>
                <div className="font-heading font-bold text-sm text-text-primary">Activity</div>
                <button
                  onClick={() => fetchLogs(false)}
                  data-testid="account-menu-logs-refresh"
                  className="text-[10px] uppercase tracking-wider text-text-muted hover:text-text-primary"
                >
                  Refresh
                </button>
              </div>

              <div className="max-h-[400px] overflow-y-auto" data-testid="account-menu-logs-list">
                {logsLoading ? (
                  <div className="px-4 py-8 text-center text-xs text-text-muted">Loading…</div>
                ) : logs.length === 0 ? (
                  <div className="px-4 py-10 text-center" data-testid="account-menu-logs-empty">
                    <Pulse size={24} className="text-text-muted/40 mx-auto mb-2" />
                    <div className="text-sm text-text-secondary">No activity yet</div>
                    <div className="text-[11px] text-text-muted mt-1">
                      As you and your team make changes,<br />
                      they'll appear here.
                    </div>
                  </div>
                ) : (
                  logs.map((l) => {
                    const Icon = ICONS[l.icon] || Pulse;
                    return (
                      <div
                        key={l.id}
                        className="px-3 py-2 border-b border-border/50 hover:bg-background transition-colors"
                        data-testid={`account-menu-log-row-${l.id}`}
                      >
                        <div className="flex items-start gap-2.5">
                          <div
                            className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
                          >
                            <Icon size={11} weight="bold" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-text-primary leading-tight">
                              <span className="font-semibold">{l.actor_name}</span>{' '}
                              <span className="text-text-secondary">{l.verb}</span>{' '}
                              {l.target_label && <span className="font-medium">{l.target_label}</span>}
                            </div>
                            <div className="text-[10px] text-text-muted mt-0.5">{timeAgo(l.created_at)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="px-3 py-2 border-t border-border bg-background">
                <div className="text-[10px] text-text-muted text-center">
                  Ask your AI assistant "<em>who did what</em>" — it can answer.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
